import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { object, string, ref } from 'yup';
import { useTranslation } from 'react-i18next';
import type { EditPasswordFormData } from '@jwp/ott-common/types/account';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import useForm, { type UseFormOnSubmitHandler } from '@jwp/ott-hooks-react/src/useForm';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

import EditAccountPasswordForm from '../../../components/EditAccountPassword/EditAccountPasswordForm';
import { useAriaAnnouncer } from '../../AnnouncementProvider/AnnoucementProvider';
import { cognitoLogout } from '@jwp/ott-common/src/services/integrations/CustomAccountService';

type Props = {
  type?: string | null;
  setShowPasswordContent: any;
};

const EditAccountPassword = ({ type, setShowPasswordContent }: Props) => {
  const accountController = getModule(AccountController);
  const { isKatonCognitoEnabled } = useAppConfig();

  const { t } = useTranslation('account');
  const announce = useAriaAnnouncer();
  const location = useLocation();
  const navigate = useNavigate();
  const resetPasswordTokenParam = useQueryParam('resetPasswordToken');
  const emailParam = useQueryParam('email');
  const email = useAccountStore((state) => state.user?.email);
  const user = useAccountStore.getState().user;

  const passwordSubmitHandler: UseFormOnSubmitHandler<EditPasswordFormData> = async (formData, { setErrors, setSubmitting }) => {
    const { oldPassword, password, passwordConfirmation, resetPasswordToken } = formData;

    let resetToken = resetPasswordTokenParam;
    if (resetPasswordToken) {
      resetToken = resetPasswordToken;
    }
    try {
      if (user && !resetToken) {
        await accountController.changePasswordWithOldPassword(oldPassword || '', password, passwordConfirmation);
      } else {
        if (!resetToken) {
          setErrors({ form: t('reset.invalid_link') });

          return setSubmitting(false);
        }
        await accountController.changePasswordWithToken(emailParam || '', password, resetToken, passwordConfirmation);
      }

      announce(t('reset.password_reset_success'), 'success');
      if (isKatonCognitoEnabled) {
        await cognitoLogout();
      } else {
        await accountController.logout();
      }
      navigate(modalURLFromLocation(location, 'reset-password-success'));
    } catch (error: unknown) {
      if (error instanceof Error) {
        if (isKatonCognitoEnabled) {
          if (error.message.includes('Incorrect old password')) {
            setErrors({ oldPassword: error.message });
          } else {
            setErrors({ form: error.message });
          }
        } else {
          if (error.message.includes('invalid param password')) {
            setErrors({ password: t('reset.invalid_password') });
          } else if (error.message.includes('resetPasswordToken is not valid')) {
            setErrors({ form: t('reset.invalid_reset_link') });
          } else if (error.message.includes('score does not match standards')) {
            setErrors({ form: t('reset.password_strength') });
          } else if (error.message.includes('password could not be set')) {
            setErrors({ form: t('reset.invalid_token') });
          } else if (error.message.includes('The old password does not match.')) {
            setErrors({ oldPassword: t('reset.old_password_wrong') });
          }
        }
      } else {
        const fallbackMessage = typeof error === 'string' ? error : error instanceof Error ? error.message : JSON.stringify(error);
        setErrors({ form: fallbackMessage });
      }
    }
    setSubmitting(false);
  };

  const passwordForm = useForm<EditPasswordFormData>({
    initialValues: { password: '', passwordConfirmation: '' },
    validationSchema: object().shape({
      email: string(),
      oldPassword: string().required(t('login.field_required', { field: t('reset.current_password') })),
      password: string()
        .required(t('login.field_required', { field: t('reset.new_password') }))
        .min(8, t('registration.password_too_short'))
        .matches(/[A-Za-z]/, t('registration.password_one_letter'))
        .matches(/\d/, t('registration.password_one_number'))
        .matches(/[%\-_!@#$*=&.]/, t('registration.password_special_character')),
      passwordConfirmation: string()
        .required(t('login.field_required', { field: t('reset.confirm_password') }))
        .oneOf([ref('password')], t('reset.passwords_do_not_match')),
      resetPasswordToken: string(),
    }),
    validateOnBlur: true,
    onSubmit: passwordSubmitHandler,
  });

  const { values, validateField } = passwordForm;

  useEffect(() => {
    if (values.passwordConfirmation) {
      validateField('passwordConfirmation', values);
    }
  }, [values.password, values.passwordConfirmation, validateField, values]);

  const resendEmailClickHandler = async () => {
    try {
      if (email) {
        await accountController.resetPassword(email, '');
      }
    } catch (error: unknown) {
      passwordForm.setErrors({ form: t('user:account.resend_mail_error') });
    }
  };

  return (
    <EditAccountPasswordForm
      value={passwordForm.values}
      submitting={passwordForm.submitting}
      onChange={passwordForm.handleChange}
      onBlur={passwordForm.handleBlur}
      errors={passwordForm.errors}
      onSubmit={passwordForm.handleSubmit}
      validationError={passwordForm.validationSchemaError}
      showOldPasswordField={!!(user && !resetPasswordTokenParam)}
      showResetTokenField={type === 'add' || (!user && !resetPasswordTokenParam)}
      email={emailParam || email}
      onResendEmailClick={resendEmailClickHandler}
      setShowPasswordContent={setShowPasswordContent}
    />
  );
};

export default EditAccountPassword;
